export const DEFAULT_REPORT_OUTLINE = [
  {
    id: 'introduction',
    title: 'Introduction',
    intent: 'Establish target audience and quality standards',
  },
  {
    id: 'what-we-looked-for',
    title: 'What We Looked For',
    intent: 'Define evaluation criteria and quality thresholds',
  },
  {
    id: 'quick-picks',
    title: 'Quick Picks',
    intent: 'Provide at-a-glance category winners',
  },
  {
    id: 'products',
    title: 'The Products',
    intent: 'Detailed product assessments with pros/cons',
  },
  {
    id: 'video-insights',
    title: 'Video Reviews',
    intent: 'Key takeaways from hands-on video reviews',
  },
  {
    id: 'social-insights',
    title: 'Community Insights',
    intent: 'Notable owner feedback and recurring themes from the community',
  },
  {
    id: 'how-to-choose',
    title: 'How to Choose',
    intent: 'Decision framework based on use case priority',
  },
  {
    id: 'evidence-snapshot',
    title: 'Evidence Snapshot',
    intent:
      'Open-ended visual summary of methodology confidence and supporting evidence',
  },
  {
    id: 'our-recommendation',
    title: 'Our Recommendation',
    intent:
      'Surface top pick and smart money alternative with clear purchase path',
  },
  {
    id: 'final-recommendations',
    title: 'Final Recommendations',
    intent: 'Synthesis and trade-off guidance',
  },
  {
    id: 'sources',
    title: 'Sources',
    intent: 'Citation transparency',
  },
] as const

export type DefaultReportOutlineSection =
  (typeof DEFAULT_REPORT_OUTLINE)[number]

const SECTION_ALIASES: Record<string, DefaultReportOutlineSection['id']> = {
  // Historical ids
  overview: 'introduction',
  intro: 'introduction',
  introduction: 'introduction',

  methodology: 'what-we-looked-for',
  'evaluation-criteria': 'what-we-looked-for',
  criteria: 'what-we-looked-for',
  'what-we-looked-for': 'what-we-looked-for',

  'quick-picks': 'quick-picks',
  'top-picks': 'quick-picks',
  'top-recommendations': 'quick-picks',

  products: 'products',
  'product-grid': 'products',
  'product-cards': 'products',
  'detailed-reviews': 'products',
  'detailed-analysis': 'products',
  'detailed-review': 'products',

  'video-insights': 'video-insights',
  'video-reviewer': 'video-insights',
  'video-reviews': 'video-insights',
  'video-review': 'video-insights',
  youtube: 'video-insights',
  videos: 'video-insights',

  'social-insights': 'social-insights',
  'social-signals': 'social-insights',
  'social-signal': 'social-insights',
  community: 'social-insights',
  reddit: 'social-insights',

  'how-to-choose': 'how-to-choose',
  'buying-guide': 'how-to-choose',

  'evidence-snapshot': 'evidence-snapshot',
  'evidence-summary': 'evidence-snapshot',
  'confidence-dashboard': 'evidence-snapshot',

  'our-recommendation': 'our-recommendation',
  'top-recommendation': 'our-recommendation',
  recommendation: 'our-recommendation',

  'final-recommendations': 'final-recommendations',
  conclusion: 'final-recommendations',

  sources: 'sources',
  citations: 'sources',
  'sources-section': 'sources',
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

/**
 * Canonicalize report section ids to a stable, deterministic set.
 *
 * This is used across the system to prevent drift between older outlines
 * ("overview", "buying-guide", etc.) and newer canonical ids.
 */
export function canonicalizeReportSectionId(value: string): string {
  const slug = slugify(value)
  return SECTION_ALIASES[slug] ?? slug
}

export function canonicalizeReportOutlineSections<
  T extends { id?: string | null; title?: string | null },
>(sections: T[]): Array<T & { id: string }> {
  const seen = new Set<string>()
  return sections
    .map((section) => {
      const base = section.id || section.title || ''
      const canonical = canonicalizeReportSectionId(base)
      return {
        ...section,
        id: canonical,
      } as T & { id: string }
    })
    .filter((section) => {
      if (!section.id) return false
      if (seen.has(section.id)) return false
      seen.add(section.id)
      return true
    })
}
